import { useState } from 'react';
import { PageHeader } from '@nimbitlabs/layouts';
import { useIncidentsStore } from '@/stores/incidentsStore';
import { CreateIncidentModal } from '@/components/CreateIncidentModal';
import { UpdateIncidentModal } from '@/components/UpdateIncidentModal';
import type { Incident } from '@/types/incident';
import { IncidentSeverity, IncidentStatus } from '@/types/api';
import { AlertCircle, AlertTriangle, CheckCircle, Plus, User, Clock, Search, Filter } from 'lucide-react';
import { PermissionGate } from '@nimbitlabs/features';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Tabs, TabsContent, TabsList, TabsTrigger, useToast } from '@nimbitlabs/ui';

export default function Incidents() {
  const { toast } = useToast();
  const { incidents, loading, fetchIncidents: refetch } = useIncidentsStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<IncidentSeverity | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | 'all'>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const handleIncidentClick = (incident: Incident) => {
    setSelectedIncident(incident);
    setIsUpdateModalOpen(true);
  };

  const handleIncidentCreated = () => {
    toast({ title: 'Success', description: 'Incident created successfully' });
  };

  const handleIncidentUpdated = () => {
    toast({ title: 'Success', description: 'Incident updated successfully' });
  };

  const handleIncidentDeleted = () => {
    toast({ title: 'Success', description: 'Incident deleted successfully' });
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      Critical: 'bg-destructive/10 text-destructive border-destructive/20',
      High: 'bg-warning/10 text-warning border-warning/20',
      Medium: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      Low: 'bg-info/10 text-info border-info/20',
    };
    return colors[severity] || 'bg-muted';
  };

  const getStatusBadge = (endTime: string | null | undefined) => {
    if (endTime) {
      return 'bg-success/10 text-success border-success/20';
    }
    return 'bg-warning/10 text-warning border-warning/20';
  };

  // Filter incidents based on search and filters
  const filteredIncidents = incidents.filter((incident) => {
    const matchesSearch = searchQuery === '' || 
      incident.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      incident.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSeverity = severityFilter === 'all' || incident.severity === severityFilter;
    const matchesStatus = statusFilter === 'all' || incident.status === statusFilter;
    
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const activeIncidents = filteredIncidents.filter(i => !i.endTime);
  const resolvedIncidents = filteredIncidents.filter(i => i.endTime);

  // Calculate average resolution time
  const calculateAvgResolutionTime = () => {
    const resolved = incidents.filter(i => i.endTime && i.startTime);
    if (resolved.length === 0) return 'N/A';
    
    const totalMinutes = resolved.reduce((sum, incident) => {
      const start = new Date(incident.startTime).getTime();
      const end = new Date(incident.endTime!).getTime();
      return sum + (end - start) / (1000 * 60); // Convert to minutes
    }, 0);
    
    const avgMinutes = totalMinutes / resolved.length;
    
    if (avgMinutes < 60) {
      return `${Math.round(avgMinutes)}m`;
    } else if (avgMinutes < 1440) {
      return `${(avgMinutes / 60).toFixed(1)}h`;
    } else {
      return `${(avgMinutes / 1440).toFixed(1)}d`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading incidents...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <PageHeader
        title="Incident Management"
        description="Track and resolve incidents across your services"
        actions={
          <PermissionGate permission="incidents:create">
            <Button 
              className="w-full sm:w-auto" 
              onClick={() => setIsCreateModalOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Incident
            </Button>
          </PermissionGate>
        }
      />

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search incidents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={severityFilter} onValueChange={(value) => setSeverityFilter(value as IncidentSeverity | 'all')}>
              <SelectTrigger className="w-full md:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value={IncidentSeverity.Critical}>Critical</SelectItem>
                <SelectItem value={IncidentSeverity.High}>High</SelectItem>
                <SelectItem value={IncidentSeverity.Medium}>Medium</SelectItem>
                <SelectItem value={IncidentSeverity.Low}>Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as IncidentStatus | 'all')}>
              <SelectTrigger className="w-full md:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value={IncidentStatus.Investigating}>Investigating</SelectItem>
                <SelectItem value={IncidentStatus.Identified}>Identified</SelectItem>
                <SelectItem value={IncidentStatus.Monitoring}>Monitoring</SelectItem>
                <SelectItem value={IncidentStatus.Resolved}>Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
            <div className="p-2 rounded-lg bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1 text-red-600">{activeIncidents.length}</div>
            <p className="text-sm text-muted-foreground">Open incidents</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">SEV1</CardTitle>
            <div className="p-2 rounded-lg bg-orange-500/10">
              <AlertCircle className="h-5 w-5 text-orange-600" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1">
              {incidents.filter(i => i.severity === 'Critical' && i.status !== 'Resolved').length}
            </div>
            <p className="text-sm text-muted-foreground">Critical incidents</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg Resolution Time</CardTitle>
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1">{calculateAvgResolutionTime()}</div>
            <p className="text-sm text-muted-foreground">Average time</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Resolved Today</CardTitle>
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1 text-green-600">
              {resolvedIncidents.filter(i => {
                const today = new Date().setHours(0, 0, 0, 0);
                return i.endTime && new Date(i.endTime).getTime() >= today;
              }).length}
            </div>
            <p className="text-sm text-muted-foreground">Resolved incidents</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active">
        <div className="w-full overflow-x-auto py-3 px-1 sm:px-0">
          <div className="inline-flex w-max min-w-full md:min-w-0 rounded-xl bg-card shadow-soft p-[4px]">
            <TabsList className="inline-flex h-12 items-center justify-start gap-2 rounded-xl bg-gradient-to-r from-muted/40 via-muted/30 to-muted/40 p-1.5 backdrop-blur-sm w-full">
            <TabsTrigger 
              value="active"
              className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            >
              <span className="relative z-10">Active ({activeIncidents.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="resolved"
              className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            >
              <span className="relative z-10">Resolved ({resolvedIncidents.length})</span>
          </TabsTrigger>
          </TabsList>
          </div>
        </div>

        <TabsContent value="active" className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-sm text-muted-foreground">Loading incidents...</div>
              </div>
            ) : activeIncidents.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No active incidents</p>
                </CardContent>
              </Card>
            ) : (
              activeIncidents.map(incident => (
                <Card 
                  key={incident.id} 
                  className="border-l-4 cursor-pointer hover:shadow-lg transition-shadow" 
                  style={{
                    borderLeftColor: incident.severity === 'Critical' ? 'hsl(var(--destructive))' : 'hsl(var(--warning))'
                  }}
                  onClick={() => handleIncidentClick(incident)}
                >
                  <CardHeader>
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                        <div className="space-y-2 flex-1 w-full">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${getSeverityColor(incident.severity)}`}>
                              {incident.severity.toUpperCase()}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${getStatusBadge(incident.endTime)}`}>
                              {incident.endTime ? 'Resolved' : incident.status}
                            </span>
                          </div>
                          <CardTitle>{incident.title}</CardTitle>
                          <CardDescription>{incident.description}</CardDescription>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            {incident.serviceName && (
                              <div className="flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                <span className="truncate">{incident.serviceName}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span className="truncate">{incident.assignedToUserName || 'Unassigned'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span className="text-xs">{new Date(incident.startTime).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                  </CardHeader>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="resolved" className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-sm text-muted-foreground">Loading incidents...</div>
              </div>
            ) : resolvedIncidents.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No resolved incidents</p>
                </CardContent>
              </Card>
            ) : (
              resolvedIncidents.map(incident => (
                <Card 
                  key={incident.id} 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleIncidentClick(incident)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${getSeverityColor(incident.severity)}`}>
                          {incident.severity.toUpperCase()}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border bg-success/10 text-success border-success/20">
                          Resolved
                        </span>
                      </div>
                      <CardTitle>{incident.title}</CardTitle>
                      <CardDescription>
                        {incident.description}
                      </CardDescription>
                      <div className="text-sm text-muted-foreground mt-2">
                        Resolved at {incident.endTime && new Date(incident.endTime).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <CreateIncidentModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onIncidentCreated={handleIncidentCreated}
      />

      <UpdateIncidentModal
        open={isUpdateModalOpen}
        onOpenChange={setIsUpdateModalOpen}
        incident={selectedIncident}
        onIncidentUpdated={handleIncidentUpdated}
        onIncidentDeleted={handleIncidentDeleted}
      />
    </div>
  );
}
